import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LLENC } from '../../tema';
import { textRic } from '../../textRic';

// L'AJUDA DE CADA ETAPA DEL SSM.
//
// ── QUÈ EXPLICA, I QUÈ NO ────────────────────────────────────────────────────────────────────
//
// **No explica com omplir les caselles.** Això ja ho diu cada pantalla amb els seus marcadors i
// els seus avisos. Aquí s'explica **què és l'etapa conceptualment**: què fa, què implica, què hi
// va i quin és el parany.
//
// El motiu és que el SSM parla amb paraules que semblen del llenguatge comú i no ho són.
// «L'embolic» no vol dir un embolic; és el *mess* d'Ackoff, un nus de problemes que no es poden
// resoldre per separat. Una «imatge rica» no és una imatge bonica. Una «definició arrel» no
// defineix res del món. **Qui hi arriba sense saber-ho es pensa que ho entén, que és pitjor que
// no entendre-ho.**
//
// Per això cada etapa té tres blocs —què és, què implica, el parany— i **el parany hi és a
// posta**: a totes set n'hi ha un de conegut, i dir-lo abans estalvia la meitat dels malentesos.
//
// ── EL DIBUIX ────────────────────────────────────────────────────────────────────────────────
//
// Ensenya **on ets del cicle** i, sobretot, **de quina banda de la línia**. Les etapes 3 i 4
// queden sota, i que hi quedin és el que impedeix confondre un model conceptual amb com haurien
// de funcionar les coses. És la mateixa idea que la barra lateral, dita des d'un altre lloc.

const SOTA = ['root_definitions', 'conceptual_models'];

// El cicle en petit. Set caselles, la línia, i la fletxa de tornada de la 7 a la 1 —que és el que
// fa que això sigui un cicle i no una llista.
function Cicle({ etapes, actual }) {
  const { t } = useTranslation();
  const AMPLE = 62, ALT = 30, SEP = 12;
  const y = (id) => (SOTA.includes(id) ? 58 : 8);
  const x = (i) => i * (AMPLE + SEP) + 6;
  const total = etapes.length * (AMPLE + SEP) + 12;

  return (
    <svg viewBox={`0 0 ${total} 128`} className="w-full" style={{ maxHeight: 150 }} role="img">
      {/* La línia. El que hi ha a sota no descriu el món. */}
      <line x1="0" y1="50" x2={total} y2="50" stroke={LLENC.linia} strokeWidth="1"
        strokeDasharray="6 4" opacity="0.6" />
      <text x="2" y="46" fontSize="7" fill={LLENC.textPal}>{t('ssm.realWorld')}</text>
      <text x="2" y="106" fontSize="7" fill={LLENC.textPal}>{t('ssm.systemsThinking')}</text>

      {etapes.map((e, i) => {
        const seva = e.id === actual;
        const sota = SOTA.includes(e.id);
        const seg = etapes[i + 1];
        return (
          <g key={e.id}>
            {seg && (
              <line x1={x(i) + AMPLE} y1={y(e.id) + ALT / 2} x2={x(i + 1)} y2={y(seg.id) + ALT / 2}
                stroke={LLENC.linia} strokeWidth="1" opacity="0.5" />
            )}
            <rect x={x(i)} y={y(e.id)} width={AMPLE} height={ALT} rx="4"
              fill={seva ? (sota ? LLENC.flux.farciment : LLENC.estoc.farciment) : '#FFFFFF'}
              stroke={seva ? (sota ? LLENC.flux.vora : LLENC.estoc.seleccio) : LLENC.reixa}
              strokeWidth={seva ? 2 : 1} />
            <text x={x(i) + AMPLE / 2} y={y(e.id) + 12} textAnchor="middle" fontSize="7.5"
              fontWeight={seva ? 700 : 400} fill={seva ? LLENC.text : LLENC.textPal}>
              {i + 1}
            </text>
            <text x={x(i) + AMPLE / 2} y={y(e.id) + 22} textAnchor="middle" fontSize="7"
              fill={seva ? LLENC.text : LLENC.textPal}>
              {(e.name || '').slice(0, 15)}
            </text>
          </g>
        );
      })}

      {/* La tornada de la 7 a la 1: actuar canvia la situació, i el que queda és una de nova. */}
      <path d={`M ${x(etapes.length - 1) + AMPLE / 2} ${y(etapes[etapes.length - 1].id) + ALT}
                C ${x(etapes.length - 1)} 124, ${x(0)} 124, ${x(0) + AMPLE / 2} ${ALT + 8}`}
        fill="none" stroke={LLENC.flux.vora} strokeWidth="1.2" strokeDasharray="4 3" />
      <polygon points="0,0 -5,-2.5 -5,2.5" fill={LLENC.flux.vora}
        transform={`translate(${x(0) + AMPLE / 2},${ALT + 9}) rotate(-90)`} />
    </svg>
  );
}

export default function AjudaEtapa({ etapa, etapes, onTancar }) {
  const { t } = useTranslation();
  if (!etapa) return null;
  const n = etapes.findIndex((e) => e.id === etapa.id) + 1;
  const sota = SOTA.includes(etapa.id);

  const bloc = (titol, clau) => (
    <section>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{titol}</h4>
      <p className="text-sm text-gray-700 leading-relaxed"
         dangerouslySetInnerHTML={{ __html: textRic(t(clau)) }} />
    </section>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onTancar} />
        <div className="relative bg-white rounded-md shadow-xl max-w-2xl w-full">
          <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200">
            <div>
              <span className="text-xs font-medium text-gray-500">
                {t('ssm.stageN', { n })} · {t(sota ? 'ssm.systemsThinking' : 'ssm.realWorld')}
              </span>
              <h3 className="text-lg font-semibold text-gray-900">{etapa.name}</h3>
            </div>
            <button type="button" onClick={onTancar} aria-label={t('help.close')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 flex-shrink-0">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            {bloc(t('ssm.ajQue'), `ssm.aj${n}Que`)}
            {bloc(t('ssm.ajImplica'), `ssm.aj${n}Implica`)}
            <section className="bg-warn-50 border border-warn-400 rounded-md p-3">
              <h4 className="text-xs font-semibold text-warn-700 uppercase tracking-wider mb-1.5">
                {t('ssm.ajParany')}
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed"
                 dangerouslySetInnerHTML={{ __html: textRic(t(`ssm.aj${n}Parany`)) }} />
            </section>
            <section>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {t('ssm.ajOnEts')}
              </h4>
              <Cicle etapes={etapes} actual={etapa.id} />
            </section>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button type="button" onClick={onTancar}
              className="px-4 py-2 bg-primary-700 text-white rounded-md text-sm font-semibold hover:bg-primary-800">
              {t('ssm.ajTanca')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Els textos porten `**això**` per marcar el que importa. Es converteix a `<strong>` i **s'escapa
// tota la resta**: el text ve del diccionari, que és nostre, però passar-lo per `innerHTML` sense
// escapar seria obrir una porta per una comoditat.

